import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import { ModalService } from '../../../core/services/modal.service';
import { InvoiceService } from '../../../core/services/invoice.service';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../shared/ui/toast/toast.service';
import { Invoice } from '../../../core/models/invoice.model';

import { Functions, httpsCallable } from '@angular/fire/functions';
import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { FormFieldComponent } from '../../../shared/ui/forms/form-field/form-field.component';
import { InputComponent } from '../../../shared/ui/forms/input/input.component';
import { CheckboxComponent } from '../../../shared/ui/forms/checkbox/checkbox.component';
import { TextComponent } from '../../../shared/ui/typography/text/text.component';
import { BadgeComponent } from '../../../shared/ui/badge/badge.component';
import { LucideIconComponent } from '../../../shared/ui/lucide-icon/lucide-icon.component';

@Component({
  selector: 'app-void-invoice-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    FormFieldComponent,
    InputComponent,
    CheckboxComponent,
    TextComponent,
    BadgeComponent,
    LucideIconComponent,
  ],
  templateUrl: './void-invoice-modal.component.html',
})
export class VoidInvoiceModalComponent {
  private readonly fb = inject(FormBuilder);
  private readonly modalService = inject(ModalService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);

  readonly invoices = ((this.modalService.state() as any)?.data?.invoices as Invoice[]) || [];
  readonly onSuccess = (this.modalService.state() as any)?.data?.onSuccess;

  readonly canEditBilling = this.authService.canEditBilling;
  readonly isSaving = signal(false);

  readonly form: FormGroup;

  readonly totalGrossAmount = computed(() => {
    return this.invoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
  });

  readonly totalLoadCount = computed(() => {
    return this.invoices.reduce((sum, inv) => sum + (inv.loadIds?.length || 0), 0);
  });

  readonly returnToReady = signal(true);
  readonly sendVoidEmail = signal(true);
  private readonly functions = inject(Functions);

  constructor() {
    this.form = this.fb.group({
      reason: ['', [Validators.required, Validators.minLength(3)]],
    });

    if (!this.canEditBilling()) {
      this.form.disable();
    }
  }

  close() {
    this.modalService.close();
  }

  async confirmVoid() {
    if (this.form.invalid || !this.canEditBilling()) return;

    this.isSaving.set(true);
    const reason = this.form.get('reason')?.value?.trim() || 'Voided by accounting';
    const returnToReady = this.returnToReady();

    try {
      for (const inv of this.invoices) {
        await this.invoiceService.voidInvoice(inv.id, inv.loadIds || [], reason, returnToReady);

        const targetEmail = inv.billedToEmail || inv.recipientEmail;
        if (this.sendVoidEmail() && targetEmail) {
          try {
            const emailLoadDocs = httpsCallable(this.functions, 'emailLoadDocuments');
            const companyId = this.authService.activeCompanyId();
            await emailLoadDocs({
              invoiceNumber: inv.invoiceNumber,
              companyId,
              recipientEmail: targetEmail,
              emailAction: 'VOID',
              changeNotes: reason,
            });
          } catch (emailErr: any) {
            console.warn('Failed to send void email notice', emailErr);
          }
        }
      }

      this.toastService.show(
        'success',
        this.invoices.length === 1
          ? `Invoice ${this.invoices[0].invoiceNumber} voided. Associated loads ${
              returnToReady ? 'restored to Ready to Invoice.' : 'marked as Do Not Invoice.'
            }`
          : `${this.invoices.length} invoices voided. Associated loads ${
              returnToReady ? 'restored to Ready to Invoice.' : 'marked as Do Not Invoice.'
            }`
      );

      this.modalService.close();
      this.onSuccess?.();
    } catch (err: any) {
      console.error('Failed to void invoice(s):', err);
      this.toastService.show('error', err.message || 'Failed to void invoice(s).');
    } finally {
      this.isSaving.set(false);
    }
  }
}
